import { Stack, Typography } from '@mui/material';
import { resetPassword } from 'API/user';
import { ErrorMessages, Routes } from 'enums';
import TokenAlreadyUsedDialog from 'features/account/user/token-already-used-dialog';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useErrorStore from 'stores/error-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../features/ui/base-components/pai-button';
import PaiTextField from '../features/ui/base-components/pai-text-field';

const ResetPasswordEnterNew: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [newPassword, setNewPassword] = useState('');
  const [newPasswordRepeated, setNewPasswordRepeated] = useState('');
  const [loading, setLoading] = useState(false);

  const [tokenExpiredHelperText, setTokenExpiredHelperText] = useState('');
  const [newPasswordHelperText, setNewPasswordHelperText] = useState('');
  const [newPasswordRepeatedHelperText, setNewPasswordRepeatedHelperText] = useState('');
  const [invalidRequestHelperText, setInvalidRequestHelperText] = useState('');

  const [tokenAlreadyUsedDialog, setTokenAlreadyUsedDialog] = useState<{ open: boolean }>({ open: false });

  useEffect(() => setNewPasswordHelperText(''), [newPassword]);
  useEffect(() => setNewPasswordRepeatedHelperText(''), [newPasswordRepeated]);

  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
  };

  const handleNewPasswordRepeatedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPasswordRepeated(event.target.value);
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6 || newPassword.length > 50) {
      setNewPasswordHelperText('*' + t('passwordLength'));
      if (newPasswordRepeated.length < 6 || newPasswordRepeated.length > 50) {
        setNewPasswordRepeatedHelperText('*' + t('passwordLength'));
      }
      return;
    }
    if (!_.isEqual(newPassword, newPasswordRepeated)) {
      setNewPasswordRepeatedHelperText('*' + t('passNoMatch'));
      return;
    }

    const query = new URLSearchParams(location.search);
    const resetToken = query.get('resetToken');
    const userId = query.get('userId');

    if (!userId || !resetToken) {
      setInvalidRequestHelperText('*' + t('invalidRequestUserIdOrResetTokenMissing'));
      return;
    }

    setLoading(true);
    const res = await resetPassword(newPassword, resetToken, userId);
    if (!res.error) {
      navigate(Routes.SIGNIN);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED) {
        setTokenExpiredHelperText('*' + t('resetPasswordTokenExpired'));
      } else if (res.error.code === 400 && res.error.message === ErrorMessages.TOKEN_ALREADY_USED) {
        handleOpenTokenAlreadyUsedDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/auth/reset-password', 'post');
      }
    }
    setLoading(false);
  };

  /**
   * Token already used dialog handlers
   */

  const handleOpenTokenAlreadyUsedDialog = () => {
    setTokenAlreadyUsedDialog({ open: true });
  };

  const handleCloseTokenAlreadyUsedDialog = () => {
    setTokenAlreadyUsedDialog({ open: false });
  };

  return (
    <div className={classes.resetPasswordEnterNew}>
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.headerMark} />
          <Typography className={classes.headerTitle}>{t('authResetPasswordTitle')}</Typography>
        </div>
        <div className={classes.content}>
          <Stack gap={2.5}>
            {!tokenExpiredHelperText ? (
              <Stack gap={2.5}>
                <Typography className={classes.intro}>{t('resetPasswordParagraph')}</Typography>
                <div className={classes.field}>
                  <Typography className={classes.fieldLabel}>{t('newPassword')}</Typography>
                  <PaiTextField
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    type={'password'}
                    error={newPasswordHelperText !== '' || invalidRequestHelperText !== ''}
                    errorText={
                      newPasswordHelperText !== '' ? newPasswordHelperText : invalidRequestHelperText !== '' ? invalidRequestHelperText : undefined
                    }
                  />
                </div>
                <div className={classes.field}>
                  <Typography className={classes.fieldLabel}>{t('repeatNewPassword')}</Typography>
                  <PaiTextField
                    value={newPasswordRepeated}
                    onChange={handleNewPasswordRepeatedChange}
                    type={'password'}
                    error={newPasswordRepeatedHelperText !== '' || invalidRequestHelperText !== ''}
                    errorText={
                      newPasswordRepeatedHelperText !== ''
                        ? newPasswordRepeatedHelperText
                        : invalidRequestHelperText !== ''
                        ? invalidRequestHelperText
                        : undefined
                    }
                  />
                </div>
                <PaiButton
                  text={t('resetPasswordNow')}
                  action={handleResetPassword}
                  size={'large'}
                  fullWidth={true}
                  loading={loading}
                  style={{ height: '48px', borderRadius: '8px', fontSize: '1rem' }}
                  disabled={
                    newPassword.trim() === '' ||
                    newPasswordRepeated.trim() === '' ||
                    newPasswordHelperText !== '' ||
                    newPasswordRepeatedHelperText !== '' ||
                    invalidRequestHelperText !== ''
                  }
                />
              </Stack>
            ) : (
              <Stack gap={2.5}>
                <Stack gap={1}>
                  <Typography className={classes.subTitle}>{t('resetTokenExpired')}</Typography>
                  <Typography className={classes.intro}>{t('resetTokenExpiredParagraph')}</Typography>
                </Stack>
                <PaiButton
                  text={t('requestNewEmail')}
                  action={() => navigate(Routes.FORGET_PASSWORD)}
                  size={'large'}
                  fullWidth={true}
                  style={{ height: '48px', borderRadius: '8px', fontSize: '1rem' }}
                />
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent={'center'}>
              <PaiLink text={t('backToLogin')} size={'sm'} variant={'primary'} underline={'always'} action={() => navigate(Routes.SIGNIN)} />
            </Stack>
          </Stack>
        </div>
      </div>
      <TokenAlreadyUsedDialog open={tokenAlreadyUsedDialog.open} handleClose={handleCloseTokenAlreadyUsedDialog} />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  resetPasswordEnterNew: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    background: '#FFF',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    [theme.breakpoints.down('sm')]: {
      boxShadow: 'unset',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 28px',
    borderBottom: '1px solid #F0F0F5',
  },
  headerMark: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    flexShrink: 0,
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
  headerTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
  },
  content: {
    padding: '28px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
  },
  intro: {
    fontSize: theme.typography.pxToRem(14),
    color: '#555',
    lineHeight: 1.6,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '& .MuiFormControl-root': {
      borderColor: '#E3E3E8',
      borderRadius: '8px',
    },
    '& .MuiInputBase-root': {
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      minHeight: 'calc(44px - 16px)',
    },
    '& .MuiFormControl-root:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiFormControl-root:has(.Mui-error)': {
      borderColor: theme.palette.error.main,
    },
  },
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: '#707070',
  },
}));

export default ResetPasswordEnterNew;
